import type { Config, EventBase, IdentifyPayload } from './types'
import { getAnonId, getSessionId } from './ids'
import { safeGet, safeSet, CONSENT_KEY } from './storage'
import { postJSON, detectCountry } from './transport'

const isBrowser = typeof window !== 'undefined'

function pickUTM(): Record<string, string> {
  if (!isBrowser) return {}
  const sp = new URLSearchParams(window.location.search)
  const ret: Record<string, string> = {}
  ;['utm_source','utm_medium','utm_campaign','utm_term','utm_content'].forEach(k=>{
    const v = sp.get(k); if (v) ret[k.replace('utm_','')] = v
  })
  return ret
}

function basePayload(): Partial<EventBase> {
  const lang = isBrowser ? (navigator.language || '') : ''
  const tz = isBrowser && 'Intl' in globalThis ? (Intl.DateTimeFormat().resolvedOptions().timeZone || '') : ''
  const viewport = isBrowser ? { w: window.innerWidth, h: window.innerHeight } : undefined
  const screenInfo = isBrowser ? { w: window.screen.width, h: window.screen.height } : undefined
  return { lang, tz, viewport, screen: screenInfo }
}

export class Analytics {
  private cfg: Config
  private q: EventBase[] = []
  private timer: number | null = null
  private isFlushing = false
  private anonId = ''
  private sessId = ''
  private userId: string | null = null
  private lastPageviewPath: string | null = null
  private autoPageviewInitialized = false

  constructor(cfg: Config) {
    if (!cfg.siteKey || !cfg.endpoint) throw new Error('siteKey and endpoint are required')
    this.cfg = {
      batchSize: 10,
      flushIntervalMs: 3000,
      respectDNT: true,
      autoPageview: true,
      ...cfg
    }
    this.anonId = getAnonId()
    this.sessId = getSessionId()

    if (this.cfg.autoPageview && isBrowser && !this.autoPageviewInitialized) {
      this.autoPageviewInitialized = true
      this.pageview()
      // flush on visibility change
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') this.flush()
      })
      window.addEventListener('beforeunload', () => this.flush())
    }
  }

  setConsent(status: 'granted' | 'denied') {
    safeSet(CONSENT_KEY, status)
  }

  identify(p: IdentifyPayload) {
    this.userId = p.user_id
    this.track('identify', { user_id: p.user_id, props: p.traits || {} })
  }

  pageview(props: Record<string, unknown> = {}) {
    if (!isBrowser) return
    
    const currentPath = window.location.pathname
    
    // Evita pageview duplicado na mesma página (dentro de 1 segundo)
    if (this.lastPageviewPath === currentPath) {
      const now = Date.now()
      const lastPageview = this.q.find(e => 
        e.event === 'page_view' && 
        e.path === currentPath && 
        e.ts && (now - e.ts) < 1000
      )
      if (lastPageview) {
        return
      }
    }
    
    this.lastPageviewPath = currentPath
    
    const e: EventBase = {
      event: 'page_view',
      ts: Date.now(),
      url: window.location.href,
      path: currentPath,
      referrer: document.referrer || '',
      utm: pickUTM(),
      props
    }
    this.enqueue(e)
  }

  track(name: string, props: Record<string, unknown> = {}) {
    const url = isBrowser ? window.location.href : ''
    const path = isBrowser ? window.location.pathname : ''
    const e: EventBase = { event: name, ts: Date.now(), url, path, props }
    this.enqueue(e)
  }

  private enqueue(e: EventBase) {
    // DNT
    if (this.cfg.respectDNT && isBrowser && (navigator as any).doNotTrack === '1') return
    // Consent
    const consent = safeGet<'granted' | 'denied' | null>(CONSENT_KEY, null)
    if (consent === 'denied') return

    const payload: EventBase = {
      ...basePayload(),
      ...e,
      anon_id: this.anonId,
      session_id: this.sessId,
      user_id: this.userId ?? undefined
    }
    
    // Verifica se já existe evento idêntico na fila (dentro de 500ms)
    const now = Date.now()
    const isDuplicate = this.q.some(existing => {
      if (existing.event !== payload.event || existing.path !== payload.path) return false
      if (!existing.ts || !payload.ts) return false
      const timeDiff = Math.abs((payload.ts || now) - existing.ts)
      if (timeDiff > 500) return false // Apenas dentro de 500ms
      
      // Para eventos customizados, compara props
      if (payload.event !== 'page_view') {
        const existingProps = JSON.stringify(existing.props || {})
        const newProps = JSON.stringify(payload.props || {})
        return existingProps === newProps
      }
      // Para page_view, se tem mesmo path e timestamp próximo, é duplicado
      return true
    })
    
    if (isDuplicate) {
      return
    }
    
    this.q.push(payload)
    
    if (this.q.length >= (this.cfg.batchSize || 10)) this.flush()
    else this.armTimer()
  }

  private armTimer() {
    if (this.timer) return
    this.timer = setTimeout(() => {
      this.timer = null
      this.flush()
    }, this.cfg.flushIntervalMs) as unknown as number
  }

  async flush() {
    // Proteção contra flush simultâneo
    if (this.isFlushing) {
      return
    }

    if (this.q.length === 0) return
    
    // Cancela o timer se existir, pois vamos fazer flush agora
    if (this.timer) {
      clearTimeout(this.timer)
      this.timer = null
    }
    
    this.isFlushing = true
    
    try {
      // Pega todos os itens da fila e limpa
      const batch = this.q.splice(0, this.q.length)
      const body = { site_key: this.cfg.siteKey, batch }
      
      // Detecta país e adiciona ao header
      const country = detectCountry()
      
      // SEMPRE passa headers para forçar uso de fetch (sendBeacon não suporta headers)
      // Se país não foi detectado, passa objeto vazio mas ainda força fetch
      const headers: Record<string, string> = country ? { 'x-country': country } : {}
      
      await postJSON(this.cfg.endpoint, body, this.cfg.debug, headers)
    } finally {
      this.isFlushing = false
      
      // Se novos eventos foram adicionados enquanto estava fazendo flush, agenda novo flush
      if (this.q.length > 0) {
        this.armTimer()
      }
    }
  }
}
